// src/utils/fmtAbbreviated.js
//
// LSE-TAB-REDESIGN Part 2 — abbreviated money formatter for the
// stat-card "9.3M USD" / "779K CAD" / "78.9M USD" rendering pattern.
//
// Why a separate file from fmtMoney(): fmtMoney is the precise
// 0-decimal Intl.NumberFormat used in the FinancialTermsCard ("Monthly
// rent: 779,102 USD"). The stat cards explicitly trade precision for
// glanceability (you can't compare 779,102 USD across four cards at a
// glance, but you can compare 779K / 9.3M / 75 / 78.9M).
//
// Currency-symbol policy: ISO 3-letter code trailing (e.g. "1.2M SGD",
// "9.3M USD"). No leading symbol.
//
// Returns null on null/undefined/NaN input so callers can branch on
// "Not specified" treatment without inventing an "—" sentinel here.

export function fmtAbbreviated(amount, currency = "USD") {
  if (amount == null) return null;
  const n = Number(amount);
  if (!Number.isFinite(n)) return null;

  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);

  if (abs < 1000) return `${sign}${Math.round(abs)} ${currency}`;
  if (abs < 1e6) return `${sign}${Math.round(abs / 1e3)}K ${currency}`;
  if (abs < 1e9) {
    // One-decimal millions (9.3M). Use toFixed(1) then strip a
    // trailing ".0" so 9M renders as "9M" not "9.0M" — matches
    // common dashboard convention.
    const v = (abs / 1e6).toFixed(1).replace(/\.0$/, "");
    return `${sign}${v}M ${currency}`;
  }
  const v = (abs / 1e9).toFixed(1).replace(/\.0$/, "");
  return `${sign}${v}B ${currency}`;
}

// KPI-tile dollar formatter: leading $ symbol, one-decimal M/B suffix, K for
// thousands, plain integer for < 1 000. Returns "—" for null/0/non-finite.
// Used by both LeasesPage (Total Annual Rent) and MarketPlanPage (Annual Cost)
// so the two tiles can never drift apart in format.
export function fmtKpiUsd(amount) {
  if (amount == null || !Number.isFinite(amount) || amount === 0) return "—";
  const abs = Math.abs(amount);
  if (abs >= 1e9) return `$${(amount / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `$${(amount / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `$${Math.round(amount / 1e3)}K`;
  return `$${Math.round(amount).toLocaleString()}`;
}
